import { useCallback } from "react";
import { useNavigate, useSearch } from "@tanstack/react-router";

// Ouverture de l'éditeur de template, portée par un param de recherche
// (`?template=<id>` ou `?template=new`) plutôt que par un store : la modale
// survit ainsi à un rechargement, le bouton Retour la ferme, et un lien vers
// un template précis se partage.
//
// Seul endroit qui connaît le nom du param — le schéma est déclaré sur la
// route racine (cf. __root.tsx) pour que ça marche depuis le canvas comme
// depuis les settings, sans dupliquer la déclaration.

const NEW_TEMPLATE = "new";

/** Les layouts d'un template : celui de son node, celui de sa window. */
const TEMPLATE_SURFACES = ["node", "window"] as const;
type TemplateSurface = (typeof TEMPLATE_SURFACES)[number];

function useTemplateEditor() {
  const navigate = useNavigate();
  // `strict: false` : ce hook est appelé depuis des routes différentes
  // (canvas, settings), le param vient de la racine dans tous les cas.
  const template = useSearch({
    strict: false,
    select: (search) => (search as { template?: string }).template,
  });
  const surface = useSearch({
    strict: false,
    select: (search) =>
      (search as { templateLayout?: TemplateSurface }).templateLayout,
  });

  /** `surface` : le layout montré d'abord, le node sinon. */
  const openTemplateEditor = useCallback(
    (templateId: string, surface?: TemplateSurface) => {
      void navigate({
        to: ".",
        search: (prev: Record<string, unknown>) => ({
          ...prev,
          template: templateId,
          templateLayout: surface,
        }),
      });
    },
    [navigate],
  );

  const closeTemplateEditor = useCallback(() => {
    void navigate({
      to: ".",
      search: (prev: Record<string, unknown>) => ({
        ...prev,
        template: undefined,
        templateLayout: undefined,
      }),
    });
  }, [navigate]);

  return {
    editingTemplateId: template,
    editingSurface: surface ?? "node",
    isCreating: template === NEW_TEMPLATE,
    openTemplateEditor,
    openTemplateCreator: () => openTemplateEditor(NEW_TEMPLATE),
    closeTemplateEditor,
  };
}

export { useTemplateEditor };
export { TEMPLATE_SURFACES };
export type { TemplateSurface };
